"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AprBandMarkerHiddenReason = exports.APR_BAND_SNAP_DISTANCE_PX = exports.APR_BAND_LABEL_BAND_PADDING_PX = exports.APR_BAND_MIN_LABEL_GAP_PX = exports.APR_BAND_THUMB_CLEARANCE_PX = exports.APR_BAND_MIN_VISIBLE_GAP_PX = exports.APR_BAND_GAP_EDGE_DISTANCE_PX = exports.APR_BAND_MIN_MARKER_GAP_PX = exports.APR_BAND_MIN_EDGE_DISTANCE_PX = void 0;
exports.getThumbAlignedXPx = getThumbAlignedXPx;
exports.isCoveredByThumb = isCoveredByThumb;
exports.layoutAprBandTrack = layoutAprBandTrack;
exports.snapToAprBandMarker = snapToAprBandMarker;
/**
 * Pixel layout for APR band markers and rate labels on a native range slider.
 *
 * Every position uses the thumb's own geometry (a thumb of width T is centered at T/2 + p * (W - T)),
 * so markers, labels and the thumb agree at both ends of the track. Text measurement and APR
 * formatting are injected so this stays a pure function of the track's measured width.
 */
/** A marker hidden this close (px) to the selectable start or the track end would mark a sliver band. */
exports.APR_BAND_MIN_EDGE_DISTANCE_PX = 6;
/** Two markers closer than this (px) read as one smudge; only the lower one is drawn. */
exports.APR_BAND_MIN_MARKER_GAP_PX = 5;
/** The striped non-selectable gap already separates bands whose boundary is this close (px) to a gap edge. */
exports.APR_BAND_GAP_EDGE_DISTANCE_PX = 4;
/** Narrower non-selectable gaps are invisible, so they cannot separate bands (base and mega-line offers sit about a dollar apart). */
exports.APR_BAND_MIN_VISIBLE_GAP_PX = 2;
/** Extra clearance (px) beyond the thumb radius before a marker's stubs reappear beside the thumb. */
exports.APR_BAND_THUMB_CLEARANCE_PX = 2;
/** Minimum horizontal space (px) between two rate labels. */
exports.APR_BAND_MIN_LABEL_GAP_PX = 8;
/** A non-current label needs its band to be this much (px) wider than the label itself. */
exports.APR_BAND_LABEL_BAND_PADDING_PX = 8;
/** While dragging, the thumb snaps to a visible marker within this distance (px). */
exports.APR_BAND_SNAP_DISTANCE_PX = 4;
var AprBandMarkerHiddenReason;
(function (AprBandMarkerHiddenReason) {
    AprBandMarkerHiddenReason["NEAR_SELECTABLE_START"] = "NEAR_SELECTABLE_START";
    AprBandMarkerHiddenReason["NEAR_TRACK_END"] = "NEAR_TRACK_END";
    AprBandMarkerHiddenReason["AT_GAP_EDGE"] = "AT_GAP_EDGE";
    AprBandMarkerHiddenReason["CROWDED"] = "CROWDED";
})(AprBandMarkerHiddenReason || (exports.AprBandMarkerHiddenReason = AprBandMarkerHiddenReason = {}));
function getThumbAlignedXPx(valueCents, geometry) {
    const { trackWidthPx, thumbWidthPx, visualMinCents, maxCents } = geometry;
    const rangeCents = maxCents - visualMinCents;
    const fraction = rangeCents <= 0 ? 0 : Math.min(1, Math.max(0, (valueCents - visualMinCents) / rangeCents));
    return thumbWidthPx / 2 + fraction * (trackWidthPx - thumbWidthPx);
}
/** Whether a track mark at `markCents` sits under the thumb at `valueCents`, so its overhang would poke out around the thumb. */
function isCoveredByThumb(markCents, valueCents, geometry) {
    return Math.abs(getThumbAlignedXPx(markCents, geometry) - getThumbAlignedXPx(valueCents, geometry)) < geometry.thumbWidthPx / 2 + exports.APR_BAND_THUMB_CLEARANCE_PX;
}
function snapToDevicePixel(px, devicePixelRatio) {
    return Math.round(px * devicePixelRatio) / devicePixelRatio;
}
function toSelectableBands(input) {
    const bands = [];
    for (const band of input.aprBands) {
        let widest = null;
        for (const segment of input.allowedSegments) {
            const startCents = Math.max(band.startCents, segment.startCents);
            const endCents = Math.min(band.endCents, segment.endCents);
            if (endCents <= startCents) {
                continue;
            }
            const span = { leftPx: getThumbAlignedXPx(startCents, input), rightPx: getThumbAlignedXPx(endCents, input) };
            if (!widest || span.rightPx - span.leftPx > widest.rightPx - widest.leftPx) {
                widest = span;
            }
        }
        if (widest) {
            bands.push({ ...band, selectableLeftPx: widest.leftPx, selectableRightPx: widest.rightPx });
        }
    }
    return bands;
}
function getMarkerHiddenReason(xPx, previousVisibleXPx, gapEdgesPx, selectableStartXPx, trackEndXPx) {
    if (xPx - selectableStartXPx < exports.APR_BAND_MIN_EDGE_DISTANCE_PX) {
        return AprBandMarkerHiddenReason.NEAR_SELECTABLE_START;
    }
    if (trackEndXPx - xPx < exports.APR_BAND_MIN_EDGE_DISTANCE_PX) {
        return AprBandMarkerHiddenReason.NEAR_TRACK_END;
    }
    if (gapEdgesPx.some((edgePx) => Math.abs(edgePx - xPx) < exports.APR_BAND_GAP_EDGE_DISTANCE_PX)) {
        return AprBandMarkerHiddenReason.AT_GAP_EDGE;
    }
    if (previousVisibleXPx !== null && xPx - previousVisibleXPx < exports.APR_BAND_MIN_MARKER_GAP_PX) {
        return AprBandMarkerHiddenReason.CROWDED;
    }
    return null;
}
function layoutMarkers(input, bands) {
    const firstSegment = input.allowedSegments[0];
    const lastSegment = input.allowedSegments[input.allowedSegments.length - 1];
    if (!firstSegment || !lastSegment) {
        return [];
    }
    const selectableStartXPx = getThumbAlignedXPx(firstSegment.startCents, input);
    const trackEndXPx = getThumbAlignedXPx(lastSegment.endCents, input);
    const gapEdgesPx = [];
    for (let index = 0; index < input.allowedSegments.length - 1; index += 1) {
        const gapStartXPx = getThumbAlignedXPx(input.allowedSegments[index].endCents, input);
        const gapEndXPx = getThumbAlignedXPx(input.allowedSegments[index + 1].startCents, input);
        if (gapEndXPx - gapStartXPx >= exports.APR_BAND_MIN_VISIBLE_GAP_PX) {
            gapEdgesPx.push(gapStartXPx, gapEndXPx);
        }
    }
    const markers = [];
    let previousVisibleXPx = null;
    for (let index = 0; index < bands.length - 1; index += 1) {
        const band = bands[index];
        const xPx = getThumbAlignedXPx(band.endCents, input);
        const hiddenReason = getMarkerHiddenReason(xPx, previousVisibleXPx, gapEdgesPx, selectableStartXPx, trackEndXPx);
        if (hiddenReason === null) {
            previousVisibleXPx = xPx;
        }
        markers.push({
            key: index,
            boundaryCents: band.endCents,
            xPx: snapToDevicePixel(xPx, input.devicePixelRatio),
            hiddenReason,
            isUnderThumb: isCoveredByThumb(band.endCents, input.valueCents, input),
        });
    }
    return markers;
}
/** Current band first, then the next step up, then outward alternating up and down. */
function getLabelPriority(bandCount, currentIndex) {
    const priority = [currentIndex];
    for (let distance = 1; distance < bandCount; distance += 1) {
        if (currentIndex + distance < bandCount) {
            priority.push(currentIndex + distance);
        }
        if (currentIndex - distance >= 0) {
            priority.push(currentIndex - distance);
        }
    }
    return priority;
}
function getNearestBandIndex(bands, valueCents) {
    let nearestIndex = 0;
    let nearestDistance = Infinity;
    bands.forEach((band, index) => {
        const distance = valueCents < band.startCents ? band.startCents - valueCents : valueCents > band.endCents ? valueCents - band.endCents : 0;
        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
        }
    });
    return nearestIndex;
}
function layoutLabels(input, bands) {
    const currentIndex = bands.findIndex((band) => input.valueCents >= band.startCents && input.valueCents <= band.endCents);
    const labels = bands.map((band, index) => ({ key: index, text: input.formatApr(band.fixedTermApr), leftPx: 0, isCurrent: index === currentIndex, isVisible: false }));
    const placed = [];
    for (const index of getLabelPriority(bands.length, currentIndex === -1 ? getNearestBandIndex(bands, input.valueCents) : currentIndex)) {
        const band = bands[index];
        const label = labels[index];
        const widthPx = input.measureLabelWidthPx(label.text);
        const centerPx = (band.selectableLeftPx + band.selectableRightPx) / 2;
        const leftPx = snapToDevicePixel(Math.min(Math.max(0, centerPx - widthPx / 2), Math.max(0, input.trackWidthPx - widthPx)), input.devicePixelRatio);
        label.leftPx = leftPx;
        if (!label.isCurrent) {
            const bandWidthPx = band.selectableRightPx - band.selectableLeftPx;
            if (bandWidthPx < widthPx + exports.APR_BAND_LABEL_BAND_PADDING_PX) {
                continue;
            }
            const crowdsPlacedLabel = placed.some((other) => leftPx < other.leftPx + other.widthPx + exports.APR_BAND_MIN_LABEL_GAP_PX && leftPx + widthPx + exports.APR_BAND_MIN_LABEL_GAP_PX > other.leftPx);
            if (crowdsPlacedLabel) {
                continue;
            }
        }
        label.isVisible = true;
        placed.push({ leftPx, widthPx });
    }
    return labels;
}
/**
 * Markers and labels for the selected group's APR bands. Markers appear only where the rate steps, so a single
 * rate across the track yields one label and no markers, keeping the rate row consistent across terms.
 */
function layoutAprBandTrack(input) {
    const bands = toSelectableBands(input);
    if (bands.length === 0 || input.trackWidthPx <= input.thumbWidthPx) {
        return { markers: [], labels: [] };
    }
    return { markers: layoutMarkers(input, bands), labels: layoutLabels(input, bands) };
}
/**
 * Snap a dragged value onto a visible marker when the thumb is within APR_BAND_SNAP_DISTANCE_PX of it.
 *
 * Lands on the highest step of the range input at or below the boundary, so the browser's step
 * sanitization (counted from the input's min) cannot push the committed value into the next rate.
 */
function snapToAprBandMarker(valueCents, markers, geometry, stepCents) {
    const valueXPx = getThumbAlignedXPx(valueCents, geometry);
    for (const marker of markers) {
        if (marker.hiddenReason !== null) {
            continue;
        }
        if (Math.abs(getThumbAlignedXPx(marker.boundaryCents, geometry) - valueXPx) <= exports.APR_BAND_SNAP_DISTANCE_PX) {
            return geometry.visualMinCents + Math.floor((marker.boundaryCents - geometry.visualMinCents) / stepCents) * stepCents;
        }
    }
    return valueCents;
}
